import React, { useEffect, useState } from 'react';
import { getPortfolio, createPortfolio, updatePortfolio, deletePortfolio, uploadFiles, deleteUploadedFile, uploadFile } from './api';

export default function PortfolioAdmin() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await getPortfolio();
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }

  function edit(item: any) { setEditing({ ...item }); }

  function resetForm() { setEditing(null); }

  async function save() {
    // basic validation
    if (!editing.title || editing.title.trim() === '') {
      alert('Title is required');
      return;
    }

    try {
      const payload = {
        id: editing.id,
        title: editing.title,
        folder: editing.folder || null,
        category: editing.category || 'Web',
        desc: editing.desc || '',
        thumbnail: editing.thumbnail || '',
        files: editing.files || []
      };

      if (editing.id) {
        await updatePortfolio(payload);
      } else {
        await createPortfolio(payload);
      }
      await load();
      resetForm();
    } catch (e) { alert('Save failed'); console.error(e); }
  }

  async function onDelete(id: number) {
    if (!window.confirm('Delete this project?')) return;
    await deletePortfolio(id);
    await load();
  }

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    try {
      const r = await uploadFiles(e.target.files);
      if (!editing) setEditing({ files: r.urls });
      else setEditing({ ...editing, files: [...(editing.files || []), ...r.urls] });
    } catch (err) { console.error(err); alert('Upload failed'); }
  }

  async function onThumbnailUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files.length === 0) return;
    try {
      const file = e.target.files[0];
      const r = await uploadFile(file);
      setEditing({ ...editing, thumbnail: r.url });
    } catch (err) { console.error(err); alert('Thumbnail upload failed'); }
  }

  async function removeFile(url: string) {
    if (!editing) return;
    const projectId = editing.id || 0;
    const resp = await deleteUploadedFile(url, projectId);
    setEditing({ ...editing, files: (editing.files || []).filter((u: string) => u !== url) });
    if (resp.deleted) {
      // file removed
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Portfolio</h2>
        <button onClick={() => setEditing({ title: '', desc: '', category: 'Web', files: [] })} className="bg-sky-600 text-white px-3 py-1 rounded">New</button>
      </div>
      {loading ? <div>Loading...</div> : (
        <div className="grid gap-3">
          {items.map(it => (
            <div key={it.id} className="p-3 border rounded flex justify-between">
              <div>
                <div className="font-medium">{it.title}</div>
                <div className="text-sm text-slate-600">{it.category}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(it)} className="px-2 py-1 border rounded">Edit</button>
                <button onClick={() => onDelete(it.id)} className="px-2 py-1 border rounded text-rose-600">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="mt-6 p-4 border rounded bg-white">
          <h3 className="mb-2">{editing.id ? 'Edit' : 'New'} Project</h3>
          <label className="block mb-1">Title</label>
          <input aria-label="title" placeholder="Project title" className="w-full p-2 border rounded mb-2" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} />
          <label className="block mb-1">Category</label>
          <input aria-label="category" placeholder="Web" className="w-full p-2 border rounded mb-2" value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} />
          <label className="block mb-1">Description</label>
          <textarea aria-label="description" placeholder="Short description" className="w-full p-2 border rounded mb-2" value={editing.desc} onChange={e => setEditing({ ...editing, desc: e.target.value })} />

          <label className="block mb-1">Files</label>
          <div className="mb-2">
            {(editing.files || []).map((u: string) => (
              <div key={u} className="flex items-center justify-between p-1">
                <a className="text-sky-600" href={u} target="_blank" rel="noreferrer">{u}</a>
                <button onClick={() => removeFile(u)} className="text-rose-600 ml-2">Remove</button>
              </div>
            ))}
          </div>
          <input aria-label="upload files" title="Upload files" type="file" multiple onChange={onUpload} className="mb-2" />

          <label className="block mb-1">Thumbnail</label>
          <div className="mb-2 flex items-center gap-4">
            {editing.thumbnail ? (
              <img src={editing.thumbnail} alt="thumbnail" className="w-24 h-16 object-cover border rounded" />
            ) : (
              <div className="w-24 h-16 bg-slate-100 flex items-center justify-center border rounded text-sm">No thumbnail</div>
            )}
            <input aria-label="upload thumbnail" title="Upload thumbnail" type="file" accept="image/*" onChange={onThumbnailUpload} />
          </div>

          <div className="flex gap-2">
            <button onClick={save} className="bg-sky-600 text-white px-3 py-1 rounded">Save</button>
            <button onClick={resetForm} className="px-3 py-1 border rounded">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
